import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  isLoading?: boolean;
  /** Rendered left of the rows-per-page select (e.g. TableZoomControls). */
  leadingControls?: ReactNode;
}

export function Pagination({
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  isLoading = false,
  leadingControls,
}: PaginationProps) {
  const { t } = useTranslation();
  const canPrevious = page > 0;
  const canNext = (page + 1) * limit < total;

  const previousPage = () => {
    if (canPrevious) onPageChange(page - 1);
  };

  const nextPage = () => {
    if (canNext) onPageChange(page + 1);
  };

  const startItem = total > 0 ? page * limit + 1 : 0;
  const endItem = Math.min((page + 1) * limit, total);

  return (
    <div className="flex items-center justify-between py-4">
      <div className="text-sm text-gray-600">
        {t('showing')} {startItem}-{endItem} {t('of')} {total}
      </div>
      <div className="flex items-center space-x-2">
        {leadingControls}
        <Select value={String(limit)} onValueChange={(val) => onLimitChange(Number(val))}>
          <SelectTrigger className="data-[placeholder]:text-black">
            <SelectValue placeholder="Items per page" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>{t('perPage')}</SelectLabel>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={previousPage}
          disabled={!canPrevious || isLoading}
          className="disabled:opacity-50"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="ml-2">{t('prev')}</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={nextPage}
          disabled={!canNext || isLoading}
          className="disabled:opacity-50"
        >
          <span className="mr-2">{t('next')}</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
